import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import Header from '../../components/Header';
import Button from '../../components/Button';
import ApplicationId from '../../components/ApplicationId';
import { parseUrlParams, addLusaHeaderParam } from '../../utils/parseUrlParams';
import { numberUnmask } from '../../utils/masks';
import {
  newNextAction,
} from '../../actions/navigation';

import './style.scss';

const OTP = (props) => {
  const match = useParams();
  const workflowType = get(match, 'workflowType') === 'luwa' ? 'dtm-luwa' : get(match, 'workflowType');
  const [isFirstAttempt, setIsFirstAttempt] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [errors, setErrors] = useState('');
  const [code1, setCode1] = useState('');
  const [code2, setCode2] = useState('');
  const [code3, setCode3] = useState('');
  const [code4, setCode4] = useState('');
  const [code5, setCode5] = useState('');
  const [code6, setCode6] = useState('');
  const code = useMemo(() => `${code1}${code2}${code3}${code4}${code5}${code6}`, [code1, code2, code3, code4, code5, code6]);
  const inputRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];
  const verificationRef = useRef();
  const params = parseUrlParams(window.location.search);

  const setCodeForIndex = (value, index) => {
    switch (index) {
      case 0:
        setCode1(value);
        break;
      case 1:
        setCode2(value);
        break;
      case 2:
        setCode3(value);
        break;
      case 3:
        setCode4(value);
        break;
      case 4:
        setCode5(value);
        break;
      case 5:
        setCode6(value);
        break;
      default:
        break;
    }
  };

  const handlePaste = (e) => {
    const data = e.target.value;
    if (data) {
      const dataArray = numberUnmask(data).substring(0, 6).split('');
      if (dataArray.length > 0) {
        dataArray.forEach((char, index) => {
          setCodeForIndex(char, index);
        });
        inputRefs[dataArray.length - 1].current.focus();
      }
    }
  };

  useEffect(() => {
    const { navigate, checkinData } = props;

    const sourceData = get(checkinData, ['data', 'data']) || get(checkinData, ['state', 'data']);
    if (!sourceData) {
      navigate(addLusaHeaderParam(`/${workflowType === 'dtm-luwa' ? 'luwa' : workflowType}/?key=${params.key}`));
    }
  }, []);

  const handleInputChange = (e) => {
    const { value } = e.target;
    if (!(e.key === 'Backspace' || e.key === 'Delete' || e.key === 'Enter') && !(/^\d*$/.test(e.target.value))) {
      return;
    }
    if (value.length > 2) {
      handlePaste(e);
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      setCodeForIndex('', index - 1);
      inputRefs[index - 1].current.focus();
    } else if (e.key === 'Backspace' && e.target.value.length > 0) {
      setCodeForIndex('', index);
    }

    if (/^\d*$/.test(e.key)) {
      setCodeForIndex(e.key, index);
      if (index < inputRefs.length - 1) {
        inputRefs[index + 1].current.focus();
      }
    }
  };

  const handleSubmit = (e) => {
    e && e.preventDefault();

    setIsSending(true);
    setErrors('');

    props.newNextAction({
      workflowType,
      key: params.key,
      data: {
        otp: code,
      },
      success: (res) => {
        const { navigate } = props;
        let routeUrl = get(res, 'data.url');
        if (routeUrl && params.lusa_header && params.lusa_header === 'false') {
          routeUrl = addLusaHeaderParam(routeUrl);
        }
        navigate(routeUrl);
        setIsSending(false);
        setIsFirstAttempt(false);
      },
      fail: (error) => {
        setIsSending(false);
        setIsFirstAttempt(false);
        if (error?.response?.data?.errorMessage) {
          setErrors(error.response.data.errorMessage);
        } else if (error?.response?.data?.errorMessages && error.response.data.errorMessages.length > 0) {
          setErrors(error.response.data.errorMessages[0]);
        } else if (error?.response?.data?.failure?.errors && error.response.data.failure.errors.length > 0) {
          setErrors(error?.response?.data?.failure?.errors[0].message);
        }
        if (verificationRef && verificationRef.current) {
          verificationRef.current.className = `${verificationRef.current.className} jiggle-animation`;
          setTimeout(() => {
            verificationRef.current.className = 'verificationCodeInput';
          }, 500);
        }
      },
    });
  };

  useEffect(() => {
    if (code && code.length === 6 && isFirstAttempt) {
      handleSubmit();
    }
  }, [code]);

  return (
    <div className="otp-screen transition-item wrapper v-center">
      <Header navigate={props.navigate} />
      {get(props.checkinData, 'attributes.entityId') ? (
        <ApplicationId id={get(props.checkinData, 'attributes.entityId')} style={{ marginTop: 10 }} />
      ) : null}
      <div className="content" style={{ minHeight: 'calc(100vh - 200px)' }}>
        {params.resend ? (
          <div className="success-container">
            We have resent your verification code
          </div>
        ) : null}
        <p>
          Please enter the verification code sent to (xxx) xxx-
          {get(props.checkinData, 'data.data.to', '').slice(-4)}
        </p>
        <form className="form" onSubmit={handleSubmit}>
          <div className="verificationCodeInput" ref={verificationRef}>
            <input
              type="text"
              inputMode="numeric"
              value={code1}
              onChange={handleInputChange}
              onKeyDown={(e) => handleKeyDown(e, 0)}
              ref={inputRefs[0]}
            />
            <input
              type="text"
              inputMode="numeric"
              value={code2}
              onChange={handleInputChange}
              onKeyDown={(e) => handleKeyDown(e, 1)}
              ref={inputRefs[1]}
            />
            <input
              type="text"
              inputMode="numeric"
              value={code3}
              onChange={handleInputChange}
              onKeyDown={(e) => handleKeyDown(e, 2)}
              ref={inputRefs[2]}
            />
            <input
              type="text"
              inputMode="numeric"
              value={code4}
              onChange={handleInputChange}
              onKeyDown={(e) => handleKeyDown(e, 3)}
              ref={inputRefs[3]}
            />
            <input
              type="text"
              inputMode="numeric"
              value={code5}
              onChange={handleInputChange}
              onKeyDown={(e) => handleKeyDown(e, 4)}
              ref={inputRefs[4]}
            />
            <input
              type="text"
              inputMode="numeric"
              value={code6}
              onChange={handleInputChange}
              onKeyDown={(e) => handleKeyDown(e, 5)}
              ref={inputRefs[5]}
            />
          </div>
          {errors ? <div className="alert-container">{errors}</div> : null}
          {!isFirstAttempt || isSending ? (
            <Button
              color="primary"
              className="btn btn-primary verify-text-btn"
              onClick={handleSubmit}
              isLoading={isSending}
              isDisabled={isSending || (code && code.length !== 6) || !code}
              title="Continue"
            />
          ) : null}
          <span onClick={() => props.navigate(addLusaHeaderParam(`/${workflowType === 'dtm-luwa' ? 'luwa' : workflowType}/resend-otp?key=${params.key}`))} className="resend-btn">Did not receive text message or need a new code?</span>
        </form>
      </div>
    </div>
  );
};

OTP.propTypes = {
  navigate: PropTypes.func.isRequired,
  checkinData: PropTypes.object.isRequired,
  newNextAction: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  fetch: state.fetch,
  checkinData: state.screen.checkinData,
});

const mapDispatchToProps = {
  newNextAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(OTP);
